import React, { useMemo } from 'react';
import KeyValueTable from './KeyValueTable';

/*
 * PanchaAngaSection — Section 2 of Pambu Sheet.
 *
 * Five rows: Tithi, Nakshatra, Yoga, Karana, Vara.
 * End times in HH:MM:SS for credibility (astrologers cross-check vs Pambu).
 * Lord/pada inline in parens; no countdown rings, no live percentage bars.
 */

const VARA_LORDS = {
  Sunday: 'Sun', Monday: 'Moon', Tuesday: 'Mars', Wednesday: 'Mercury',
  Thursday: 'Jupiter', Friday: 'Venus', Saturday: 'Saturn',
};

function timeToMinutes(timeStr) {
  if (!timeStr) return null;
  const parts = timeStr.split(':').map(Number);
  return (parts[0] || 0) * 60 + (parts[1] || 0);
}

function activeLimbKey(fiveLimbs, now) {
  if (!fiveLimbs || !now) return null;
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const candidates = [
    { key: 'tithi', end: fiveLimbs.tithi?.end_time },
    { key: 'nakshatra', end: fiveLimbs.nakshatra?.end_time },
    { key: 'yoga', end: fiveLimbs.yoga?.end_time },
    { key: 'karana', end: fiveLimbs.karana?.end_time },
  ].filter(c => c.end);

  let best = null;
  let bestDelta = Infinity;
  candidates.forEach(c => {
    const endMin = timeToMinutes(c.end);
    if (endMin === null) return;
    const delta = endMin - nowMin;
    if (delta > 0 && delta < bestDelta) {
      best = c.key;
      bestDelta = delta;
    }
  });
  return best;
}

const PanchaAngaSection = ({ fiveLimbs, now, lang = 'en' }) => {
  const activeKey = useMemo(() => activeLimbKey(fiveLimbs, now), [fiveLimbs, now]);

  if (!fiveLimbs) return null;

  const arrow = '→';

  const rows = [
    {
      key: 'tithi',
      labelTa: 'திதி',
      labelEn: 'Tithi',
      value: fiveLimbs.tithi?.name
        ? `${lang === 'ta' ? (fiveLimbs.tithi.name_ta || fiveLimbs.tithi.name) : fiveLimbs.tithi.name}${fiveLimbs.tithi.paksha ? ` (${fiveLimbs.tithi.paksha})` : ''}`
        : '—',
      endTime: fiveLimbs.tithi?.end_time ? `${arrow} ${fiveLimbs.tithi.end_time}` : null,
    },
    {
      key: 'nakshatra',
      labelTa: 'நட்சத்திரம்',
      labelEn: 'Nakshatra',
      value: fiveLimbs.nakshatra?.name
        ? `${lang === 'ta' ? (fiveLimbs.nakshatra.name_ta || fiveLimbs.nakshatra.name) : fiveLimbs.nakshatra.name}${
            fiveLimbs.nakshatra.pada || fiveLimbs.nakshatra.lord
              ? ` (Pada ${fiveLimbs.nakshatra.pada || ''}${fiveLimbs.nakshatra.lord ? ` · ${lang === 'ta' ? (fiveLimbs.nakshatra.lord_ta || fiveLimbs.nakshatra.lord) : fiveLimbs.nakshatra.lord}` : ''})`
              : ''
          }`
        : '—',
      endTime: fiveLimbs.nakshatra?.end_time ? `${arrow} ${fiveLimbs.nakshatra.end_time}` : null,
    },
    {
      key: 'yoga',
      labelTa: 'யோகம்',
      labelEn: 'Yoga',
      value: (fiveLimbs.yoga?.name ? (lang === 'ta' ? (fiveLimbs.yoga.name_ta || fiveLimbs.yoga.name) : fiveLimbs.yoga.name) : '—'),
      endTime: fiveLimbs.yoga?.end_time ? `${arrow} ${fiveLimbs.yoga.end_time}` : null,
    },
    {
      key: 'karana',
      labelTa: 'கரணம்',
      labelEn: 'Karana',
      value: (fiveLimbs.karana?.name ? (lang === 'ta' ? (fiveLimbs.karana.name_ta || fiveLimbs.karana.name) : fiveLimbs.karana.name) : '—'),
      endTime: fiveLimbs.karana?.end_time ? `${arrow} ${fiveLimbs.karana.end_time}` : null,
    },
    {
      key: 'vara',
      labelTa: 'வாரம்',
      labelEn: 'Vara',
      value: fiveLimbs.vara
        ? `${lang === 'ta' ? (fiveLimbs.vara_ta || fiveLimbs.vara) : fiveLimbs.vara}${VARA_LORDS[fiveLimbs.vara] ? ` (${VARA_LORDS[fiveLimbs.vara]})` : ''}`
        : '—',
      endTime: null,
    },
  ];

  return <KeyValueTable rows={rows} activeRowKey={activeKey} lang={lang} />;
};

export default PanchaAngaSection;
